import { ModeType, AnswerRecord } from '@/constant/schema';
import supabase from './supabase';

export interface UserQuestProgress {
  id?: number;
  user_id: string;
  quest_id: string;
  chapter_id: string;
  lesson_id: string | null;
  element_id: string;
  updated_at?: string;
  mode_type: ModeType;
  progress: number;
  user_option: AnswerRecord[];
}

// Add utility functions for handling progress-related logic
export const QuestProgressUtils = {
  /**
   * Check if a lesson is completed (progress === 100)
   */
  isCompleted: (progress: UserQuestProgress | null): boolean => {
    return progress?.progress === 100;
  },

  /**
   * Calculate progress status
   */
  getProgressStatus: (progress: UserQuestProgress | null): 'not_started' | 'in_progress' | 'completed' => {
    if (!progress || progress.progress === 0) return 'not_started';
    if (progress.progress === 100) return 'completed';
    return 'in_progress';
  }
};

export class UserQuestProgressService {
  /**
   * Upsert user quest progress (insert if not exists, update if exists)
   */
  static async upsertProgress(progress: Omit<UserQuestProgress, 'updated_at' | 'id' | 'user_option'>): Promise<UserQuestProgress | null> {
    try {
      const now = new Date().toISOString();
      if (progress.lesson_id === null) {
        progress.lesson_id = 'empty';
      }
      const progressValue = Math.min(Math.max(Math.round(progress.progress), 0), 100);

      const { data, error } = await supabase
        .from('user_quest_progress')
        .upsert({
          ...progress,
          progress: progressValue,
          updated_at: now,
        }, { onConflict: 'user_id,quest_id,chapter_id,lesson_id,mode_type' })
        .select()
        .single();
      if (error) {
        console.error('Error upserting user quest progress:', error);
        return null;
      }
      return data as UserQuestProgress;
    } catch (error) {
      console.error('Error in upsertProgress:', error);
      return null;
    }
  }

  static async upsertAnswer(answer: Omit<UserQuestProgress, 'updated_at' | 'id' | 'progress'>): Promise<UserQuestProgress | null> {
    try {
      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from('user_quest_progress')
        .upsert({
          ...answer,
          lesson_id: answer.lesson_id || 'empty',
          updated_at: now,
        }, { onConflict: 'user_id,quest_id,chapter_id,lesson_id,mode_type' })
        .select()
        .single();

      if (error) {
        console.error('Error upserting user quest progress:', error);
        return null;
      }
      return data as UserQuestProgress;
    } catch (error) {
      console.error('Error in upsertAnswer:', error);
      return null;
    }
  }

  /**
   * Get user quest progress by user_id, quest_id, chapter_id, lesson_id
   */
  static async getProgress(params: {
    user_id: string;
    quest_id: string;
    chapter_id: string;
    lesson_id?: string;
    mode_type: ModeType;
  }): Promise<UserQuestProgress | null> {
    try {
      let query = supabase
        .from('user_quest_progress')
        .select('*')
        .eq('user_id', params.user_id)
        .eq('quest_id', params.quest_id)
        .eq('chapter_id', params.chapter_id)
        .eq('mode_type', params.mode_type);

      // Add lesson_id filter if provided
      if (params.lesson_id) {
        query = query.eq('lesson_id', params.lesson_id);
      } else {
        query = query.eq('lesson_id', 'empty');
      }

      // Use maybeSingle when looking for a specific record
      const { data, error } = await query.limit(1).maybeSingle();

      if (error) {
        if (error.code !== 'PGRST116') { // not found
          console.error('Error fetching user quest progress:', error);
        }
        return null;
      }
      return data as UserQuestProgress;
    } catch (error) {
      console.error('Error in getProgress:', error);
      return null;
    }
  }

  /**
   * Get latest chapter progress by user_id and quest_id
   * Returns the chapter_id of the most recently updated progress for a quest
   */
  static async getLatestChapterForQuest(userId: string, questId: string): Promise<UserQuestProgress | null> {
    try {
      const { data, error } = await supabase
        .from('user_quest_progress')
        .select('id, quest_id, chapter_id, lesson_id, element_id, updated_at, progress, mode_type')
        .eq('user_id', userId)
        .eq('quest_id', questId)
        .order('updated_at', { ascending: false })
        .limit(1);

      if (error) {
        console.error('Error fetching latest chapter progress:', error);
        return null;
      }

      if (data && data.length > 0) {
        return data[0] as UserQuestProgress
      }

      return null;
    } catch (error) {
      console.error('Error in getLatestChapterForQuest:', error);
      return null;
    }
  }

  /**
   * Get all different quests that the user has learned
   * Returns an array of the most recently updated progress for each quest, sorted by updated_at
   */
  static async getAllLearnedProgress(userId: string, questId?: string): Promise<UserQuestProgress[]> {
    try {
      let query = supabase
        .from('user_quest_progress')
        .select('id, quest_id, chapter_id, lesson_id, element_id, updated_at, mode_type, progress')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false });

      if (questId) {
        query = query.eq('quest_id', questId);
      }

      const { data, error } = await query
        .order('updated_at', { ascending: false });

      if (error) {
        console.error('Error fetching learned quests:', error);
        return [];
      }

      // Convert Map to array and sort by updated_at
      return data as UserQuestProgress[];
    } catch (error) {
      console.error('Error in getAllLearnedQuests:', error);
      return [];
    }
  }

  static async isLessonAllModeFinished(userId: string, lessonId: string, modeTypes: ModeType[]): Promise<boolean> {
    const { data, error } = await supabase
      .from('user_quest_progress')
      .select('id, quest_id, chapter_id, lesson_id, element_id, updated_at, mode_type, progress')
      .eq('user_id', userId)
      .eq('lesson_id', lessonId)
      .order('updated_at', { ascending: false })

    if (error) {
      console.error('Error fetching learned quests:', error);
      throw error;
    }

    // Check if all specified modeTypes are completed
    return modeTypes.every(modeType =>
      data.some(item => item.mode_type === modeType && item.progress === 100)
    );
  }

  static async isLessonFinished(userId: string, lessonId: string, modeType: ModeType): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('user_quest_progress')
        .select('id, quest_id, chapter_id, lesson_id, element_id, updated_at, mode_type, progress')
        .eq('user_id', userId)
        .eq('lesson_id', lessonId)
        .eq('mode_type', modeType)
        .order('updated_at', { ascending: false })
        .single();

      if (error && error.code === 'PGRST116') { // not found
        return false;
      }
      if (error) {
        console.error('Error fetching learned quests:', error);
        throw error;
      }
      return data.progress === 100;
    } catch (error) {
      console.error('Error in getAllLearnedQuests:', error);
      throw error;
    }
  }

  static async isChapterAllFinished(userId: string, chapterId: string, lessonIds: string[], modeTypes: ModeType[]): Promise<boolean> {
    const { data, error } = await supabase
      .from('user_quest_progress')
      .select('id, quest_id, chapter_id, lesson_id, element_id, updated_at, mode_type, progress')
      .eq('user_id', userId)
      .eq('chapter_id', chapterId)
      .order('updated_at', { ascending: false });

    if (error) {
      console.error('Error fetching learned quests:', error);
      throw error;
    }

    // Check if all specified lessonIds and modeTypes are completed
    return lessonIds.every(lessonId =>
      modeTypes.every(modeType =>
        data.some(item =>
          item.lesson_id === lessonId &&
          item.mode_type === modeType &&
          item.progress === 100
        )
      )
    );
  }

  static async isChapterFinished(userId: string, chapterId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('user_quest_progress')
        .select('id, quest_id, chapter_id, lesson_id, element_id, updated_at, mode_type, progress')
        .eq('user_id', userId)
        .eq('chapter_id', chapterId)
        .order('updated_at', { ascending: false })
        .single();

      if (error && error.code === 'PGRST116') { // not found
        return false;
      }

      if (error) {
        console.error('Error fetching learned quests:', error);
        throw error;
      }
      return data.progress === 100;
    } catch (error) {
      console.error('Error in getAllLearnedQuests:', error);
      throw error;
    }
  }
}

export default UserQuestProgressService; 